import { warningNotification } from "@app/common/components/notifications";
import { fileToBase64 } from "@app/common/utils/utils";
import { FileAdditionalSupports } from "@app/services/drugDelivery/types";
import { Extension, Support } from "@app/services/supportParams/types";
import { Box, Button, FileButton, Modal, ScrollArea, Table } from "@mantine/core";
import { IconCircleCheck, IconTrash, IconUpload } from "@tabler/icons-react";
import { useEffect, useState } from "react";

export default function DeliverySupportModal({
  onClose,
  open,
  supports,
  handleChange,
  data
}: {
  onClose: () => void;
  data: FileAdditionalSupports[];
  supports?: Support[];
  open: boolean;
  handleChange: (files: FileAdditionalSupports[]) => void;
}) {
  const [files, setFiles] = useState<FileAdditionalSupports[]>(data.length > 0 ? data : []);

  useEffect(() => {
    if (data.length > 0) {
      setFiles(data);
      return;
    }

    const initialFiles = (supports ?? []).map((support) => ({
      rcsId: Number(support.id),
      file: null,
      filename: ""
    }));

    setFiles(initialFiles);
  }, [supports, data]);

  const closeModal = () => {
    setFiles([]);
    onClose();
  };

  const handleChangeFile = async (
    file: File | null,
    typeAccepted: string,
    typeByMessage: string,
    id: number
  ) => {
    if (file && typeAccepted.includes(file.type)) {
      const base64 = await fileToBase64(file);
      const customData = files.map((x) =>
        x.rcsId === id ? { ...x, file: base64, filename: file.name } : x
      );
      setFiles(customData);
    } else {
      warningNotification(
        `Los tipos de documentos permitidos son: ${typeByMessage.replace(",", ", ")}`
      );
    }
  };

  const deleteFile = (id: number) => {
    const dataFile = files.map((x) => (x.rcsId === id ? { ...x, file: null } : x));
    setFiles(dataFile);
  };

  const renderRow = (option: Support, idx: number) => {
    const hasSupport = files.some((file) => file.rcsId === option.id && file.file);
    const filename = files.find((file) => file.rcsId === option.id && file.file)?.filename;
    const extensionsAccepted = (option.extensions as Extension[])
      .map((extension) => extension.mimetype)
      .join(",");

    return (
      <FileButton
        data-testid={`deliverysupportmodal-uploadbutton-${idx}`}
        onChange={(e) => {
          handleChangeFile(
            e,
            extensionsAccepted,
            String((option.extensions as Extension[]).map((ext) => ext.extension)),
            Number(option.id)
          );
        }}
        accept={extensionsAccepted}
      >
        {(props) => (
          <Table.Tr
            key={option.id}
            data-testid={`deliverysupportmodal-row-${idx}`}
            className="pointer"
            {...props}
          >
            <Table.Td>
              <div
                className={`flex flex-column ${hasSupport && "gap-1"}`}
                data-testid={`deliverysupportmodal-filename-${idx}`}
              >
                <div>{option.filename}</div>
                <div style={{ width: "25rem" }}>
                  <span className="breakEllipsis" title={filename}>
                    <b>{filename}</b>
                  </span>
                </div>
              </div>
            </Table.Td>
            <Table.Td>
              {hasSupport ? (
                <div className="flex flex-column gap-1 justify-end items-end">
                  <IconCircleCheck color="#7bc62d" size={"30px"} />
                  <IconTrash
                    className="pointer"
                    data-testid={`deliverysupportmodal-deletefile-${idx}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteFile(Number(option.id ?? ""));
                    }}
                  />
                </div>
              ) : (
                <div className="flex justify-end">
                  <IconUpload className="pointer">Upload image</IconUpload>
                </div>
              )}
            </Table.Td>
          </Table.Tr>
        )}
      </FileButton>
    );
  };

  const renderTable = () => {
    return (
      <Table mt={"sm"} mb="sm" className="biowel-table" key={files.length}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th ta={"start"}>Documentos</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {supports?.length ? (
            supports.map(renderRow)
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={12}>
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Modal
      size={500}
      title="Soportes para dispensación"
      opened={open}
      onClose={closeModal}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Box>{renderTable()}</Box>
      <Box className="flex justify-end gap-3">
        <Button variant="outline" data-testid="delivery-support-cancel-button" onClick={closeModal}>
          Cancelar
        </Button>
        <Button
          variant="filled"
          data-testid="saveFiles"
          disabled={!files.some((file) => file.file !== null)}
          onClick={() => {
            handleChange(files);
            closeModal();
          }}
        >
          Guardar
        </Button>
      </Box>
    </Modal>
  );
}
